// Calculate relative luminance of a color (for contrast ratio calculation)
const getRelativeLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

// Calculate contrast ratio between two colors
const getContrastRatio = (l1, l2) => {
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

// Determine the best text color (black or white) for a given background color
export const getBestTextColor = (backgroundColor) => {
  // Handle invalid or empty background colors
  if (!backgroundColor || typeof backgroundColor !== "string") {
    return "#000000" // Default to black
  }

  // Convert hex to RGB
  let hex = backgroundColor.replace("#", "")
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("")
  }

  if (hex.length !== 6) {
    return "#000000" // Default to black for invalid hex
  }

  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  // Calculate relative luminance of background color
  const bgLuminance = getRelativeLuminance(r, g, b)

  // Calculate contrast ratios with black and white text
  const blackLuminance = 0 // Black has 0 luminance
  const whiteLuminance = 1 // White has 1 luminance

  const contrastWithBlack = getContrastRatio(bgLuminance, blackLuminance)
  const contrastWithWhite = getContrastRatio(whiteLuminance, bgLuminance)

  // WCAG AA standard requires 4.5:1 contrast ratio for normal text
  // We'll use a slightly lower threshold (3.5:1) for better user experience
  const threshold = 3.5

  // If both colors meet the threshold, choose the one with better contrast
  if (contrastWithBlack >= threshold && contrastWithWhite >= threshold) {
    return contrastWithBlack > contrastWithWhite ? "#000000" : "#FFFFFF"
  }

  // If only one meets the threshold, use that one
  if (contrastWithBlack >= threshold) {
    return "#000000"
  }
  if (contrastWithWhite >= threshold) {
    return "#FFFFFF"
  }

  // If neither meets the threshold, use the one with better contrast
  return contrastWithBlack > contrastWithWhite ? "#000000" : "#FFFFFF"
}

// Helper function to get text color with fallback
export const getTextColor = (backgroundColor, fallbackColor = "#000000") => {
  try {
    return getBestTextColor(backgroundColor)
  } catch (error) {
    console.warn("Error calculating text color, using fallback:", error) // eslint-disable-line no-console
    return fallbackColor
  }
}
